/* eslint semi: 1 */

import DialogPopupPage from './dialog-popup-page/dialog-popup-page-tmpl';
import _ from 'lodash';
import { t } from '@/i18n/i18ns';
import { toRaw } from 'vue';

// This is a constant required to return when you want to close the dialog
export const CLOSE_DIALOG = { closeDialogSignal: true };
export const EventTypes = {
  DialogClose: 'DialogClose',
  PageChange: 'PageChange'
};

export default {
  name: 'DialogPopup',
  components: {
    DialogPopupPage
  },
  data () {
    return {
      isMounted: false,
      isVisible: false,
      activePageId: null,
      showPrevButton: false,
      showFailButton: false,
      showNextButton: true,
      navigationPrevTitle: null,
      navigationNextTitle: null,
      navigationFailTitle: null
    };
  },
  props: {
    isOpened: {
      type: [Boolean, String],
      default: false
    },
    title: {
      type: [String, null],
      default () { return 'Dialog Popup'; }
    },
    prevButtonTitle: {
      type: [String, null],
      default () { return 'Cancel'; }
    },
    failButtonTitle: {
      type: [String, null],
      default () { return t('planUpgrade.tryAgain'); }
    },
    nextButtonTitle: {
      type: [String, null],
      default () { return 'Next'; }
    },
    progress: {
      type: Object,
      default () { return {}; }
    },
    progressScenarioId: {
      type: [String, null],
      default: null
    },
    closeOnOuterClick: {
      type: Boolean,
      default: false
    }
  },
  mounted () {
    this.isVisible = this.isOpened;
    this.isMounted = true;
  },
  unmounted () {
    this.isMounted = false;
  },
  watch: {
    isMounted: {
      immediate: true,
      async handler (newValue) {
        if (!newValue) return; // || !this.isOpened || this.activePageId !== null) return;
        await this.switchPageAction(this.getActivePage()?.pageId);
      }
    },
    isOpened: {
      immediate: true,
      async handler (newValue, oldValue) {
        if (!this.isMounted || newValue === oldValue) {
          return;
        }

        if (!this.isOpened) {
          this.closeDialog();
        } else {
          // reset state of the pages on window open
          this.isVisible = true;
          this.activePageId = null;
          await this.switchPageAction(this.getActivePage()?.pageId);
        }
      }
    }
  },
  computed: {
    progressScenario () {
      if (this.progressScenarioId && this.progress[this.progressScenarioId]) {
        return this.progress[this.progressScenarioId];
      }
      return null;
    },
    progressIndex () {
      const scenario = this.progress[this.progressScenarioId];
      if (!scenario) { return null; }

      return scenario.findIndex(e => e.id === this.activePageId) ?? null;
    }
  },
  methods: {
    /**
     * Get list of active pages (DialogPopupPage child elements)
     * and extract properties required to operate
     */
    getPages () {
      let repository = [];
      try {
        repository = this.$slots.default ? this.$slots.default() : [];
      } catch (e) {
      }
      // this is for cases when you would like to wrap pages in
      // some container, but don't want to customize each page separately
      if (Array.isArray(repository) &&
        repository.length === 1 &&
        repository[0].type.name !== 'DialogPopupPage') {
        repository = repository[0].children;
      }

      return repository
        .filter(child => child.type.name === 'DialogPopupPage')
        .map(vnode => {
          return {
            pageId: vnode.props.pageId,
            navigationNext: vnode.props.navigationNext ?? '',
            navigationPrev: vnode.props.navigationPrev ?? '',
            navigationFail: vnode.props.navigationFail ?? '',
            showPrevButton: vnode.props.showPrevButton ?? false,
            showNextButton: vnode.props.showNextButton ?? false,
            showFailButton: vnode.props.showFailButton ?? false,
            onNext: vnode.props.onNext,
            onPrev: vnode.props.onPrev,
            onFail: vnode.props.onFail,
            progressScenarioId: vnode.props.progressScenarioId,
            default: vnode.props.default
          };
        });
    },
    /**
     * Get DialogPopupPage data record by it's id or null
     */
    getPageById (pageId) {
      const result = this.getPages().find((page) => page.pageId === pageId);
      if (!result) {
        return null;
      }
      return result;
    },
    getDefaultPage (pages) {
      return pages.find(p => p.default) ?? pages[0];
    },
    /**
     * Get currently active page element details OR default [first] page if there are any
     */
    getActivePage () {
      const pages = this.getPages();
      if (pages.length === 0) {
        console.error('There are no DialogPopupPages defined');
        return null;
      }

      // set first page as an active if there's none
      if (!this.activePageId) {
        return this.getDefaultPage(pages);
      }

      return this.getPageById(this.activePageId);
    },
    /**
     * Cover click outside of the dialog canvas
     */
    outerClick (event) {
      if (this.closeOnOuterClick && event.target?.className && event.target.className.indexOf('dialog-popup-wrapper') > -1) {
        this.closeDialog(event);
      }
    },
    /**
     * Trigger close of the dialog and reset state
     */
    closeDialog (event) {
      // make it idempotent
      if (this.isVisible === false && !this.activePageId) {
        return;
      }
      this.isVisible = false;
      this.activePageId = null;

      this.$emit(EventTypes.DialogClose);
    },
    /**
     * Handle click of the Prev action button, if there's
     * no handler specified then just close the dialog
     */
    async prevAction () {
      const activePage = this.getActivePage();
      if (!activePage || !activePage.onPrev) {
        this.closeDialog();
        return;
      }

      await this.switchPageAction(await activePage.onPrev(this, activePage));
    },
    /**
     * Handle nextAction (right) button click, if no onNext hook specified then
     * close the dialog
     */
    async nextAction () {
      const activePage = this.getActivePage();

      if (!activePage || !activePage.onNext) {
        console.warn('onNext hook is not defined or there are no DialogPopupPages defined, closing the dialog');
        this.closeDialog();
        return;
      }

      await this.switchPageAction(await activePage.onNext(this, activePage));
    },
    /**
     * Handle click of the failed action button, if there's
     * no handler specified then just close the dialog
     */
    async failAction () {
      const activePage = this.getActivePage();
      if (!activePage || !activePage.onFail) {
        this.closeDialog();
        return;
      }

      await this.switchPageAction(await activePage.onFail(this, activePage));
    },
    /**
     * Switch active page on screen, change button titles or
     * if navigation buttons were not specified use default values
     * of DialogPopup component (nextButtonTitle, prevButtonTitle)
     */
    async switchPageAction (nextPageId) {
      const rawNextPageId = toRaw(nextPageId);

      if (_.isEqual(rawNextPageId, CLOSE_DIALOG)) {
        this.closeDialog();
        return;
      }

      if (rawNextPageId && this.activePageId !== rawNextPageId) {
        const nextPage = this.getPageById(rawNextPageId);
        this.activePageId = rawNextPageId;

        if (nextPage) {
          this.navigationNextTitle = nextPage.navigationNext || this.nextButtonTitle;
          this.navigationPrevTitle = nextPage.navigationPrev || this.prevButtonTitle;
          this.navigationFailTitle = nextPage.navigationFail || this.failButtonTitle;

          if (nextPage.showPrevButton) {
            this.showPrevButton = true;
          } else {
            this.showPrevButton = false;
          }

          // Set showNextButton if Boolean.By default, value is true.
          if (_.isBoolean(nextPage.showNextButton)) {
            this.showNextButton = nextPage.showNextButton;
          }

          if (nextPage.showFailButton) {
            this.showFailButton = true;
          } else {
            this.showFailButton = false;
          }

          this.$emit(EventTypes.PageChange, nextPage);
        }
      }
    }
  }
};
